import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

interface CartContextValue {
  cartItems: any[];
  addToCart: (item: any) => void;
  removeFromCart: (item: any) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextValue>({
  cartItems: [],
  addToCart: () => {},
  removeFromCart: () => {},
  clearCart: () => {},
});

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [cartItems, setCartItems] = useState<any[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const stored = localStorage.getItem("cartItems");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem("cartItems", JSON.stringify(cartItems));
    } catch {
      // Ignore storage errors (private mode, etc.)
    }
  }, [cartItems]);

  const addToCart = useCallback((item: any) => {
    if (!item) return;
    setCartItems((prev) => [...prev, item]);
  }, []);

  const removeFromCart = useCallback((item: any) => {
    if (!item) return;
    setCartItems((prev) => {
      const identifier =
        item?._id || item?.productId || item?.id || item?.product_id;

      if (identifier) {
        const index = prev.findIndex((cartItem) => {
          const cartIdentifier =
            cartItem?._id ||
            cartItem?.productId ||
            cartItem?.id ||
            cartItem?.product_id;
          return cartIdentifier === identifier;
        });

        if (index === -1) return prev;
        const updated = [...prev];
        updated.splice(index, 1);
        return updated;
      }

      const index = prev.indexOf(item);
      if (index === -1) return prev;
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  }, []);

  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  const value = useMemo(
    () => ({ cartItems, addToCart, removeFromCart, clearCart }),
    [cartItems, addToCart, removeFromCart, clearCart]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
